import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, Button, IconButton, Dialog, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Chip, Alert, Tooltip, CircularProgress, InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import http, { apiErrorMessage } from '../../../Api/http';
import { getPayrollInputs, putPayrollInputs } from '../../../Api/Api';
import { TableRowsSkeleton } from '../../ContentLoader';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const PRIMARY_DARK = '#0E4A75';
const PRIMARY_BORDER = '#B3D3E8';

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const num = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v) || 0);

// The editable fields of a row, as strings so a cleared input stays cleared.
const toDraft = (r) => ({
    reportingDaysOverride: r.reportingDaysOverride ?? '',
    otDays: r.otDays ? String(r.otDays) : '',
    arrears: r.arrears ? String(r.arrears) : '',
    otherAdditions: r.otherAdditions ? String(r.otherAdditions) : '',
    otherRecoveries: r.otherRecoveries ? String(r.otherRecoveries) : '',
    remarks: r.remarks ?? '',
});

const isDirty = (draft, original) => {
    const o = toDraft(original);
    return Object.keys(o).some(k => String(draft[k] ?? '') !== String(o[k] ?? ''));
};

const cellInput = {
    '& .MuiOutlinedInput-root': { fontSize: 12, height: 30, borderRadius: '6px', bgcolor: '#fff' },
    '& input': { py: 0.4, px: 1 },
};

/**
 * HR's grid between "Lock attendance" and "Calculate".
 * Editable only while the cycle is AttendanceLocked (the API says so via `editable`).
 * `onCalculate` is called after a successful save when the user chooses Save & Calculate.
 */
export default function PayrollInputsDialog({ open, onClose, payoutMonth, monthLabel, onCalculate, calculating }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [errors, setErrors] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        setErrors([]);
        try {
            const res = await http.get(getPayrollInputs, { params: { payoutMonth } });
            if (res.data && !res.data.error) {
                const d = res.data.data;
                setData(d);
                const next = {};
                (d.rows || []).forEach(r => { next[r.employeeCode] = toDraft(r); });
                setDrafts(next);
            } else {
                setData(null);
                setLoadError(res.data?.message || 'Failed to load payroll inputs');
            }
        } catch (err) {
            setData(null);
            setLoadError(apiErrorMessage(err, 'Failed to load payroll inputs'));
        } finally {
            setLoading(false);
        }
    }, [payoutMonth]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const rows = useMemo(() => data?.rows || [], [data]);
    const editable = !!data?.editable;
    const missing = data?.missingSalaryStructure || [];

    const dirtyRows = useMemo(
        () => rows.filter(r => drafts[r.employeeCode] && isDirty(drafts[r.employeeCode], r)),
        [rows, drafts],
    );

    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            (r.name || '').toLowerCase().includes(q) || (r.employeeCode || '').toLowerCase().includes(q));
    }, [rows, search]);

    const setField = (code, field, value) => {
        setDrafts(prev => ({ ...prev, [code]: { ...prev[code], [field]: value } }));
    };

    // Client-side mirror of the API's rules so the user sees problems before saving.
    const rowProblems = (r, d) => {
        const p = [];
        const max = Number(r.maxDays) || 30;
        if (d.reportingDaysOverride !== '' && (num(d.reportingDaysOverride) < 0 || num(d.reportingDaysOverride) > max)) p.push(`reporting days must be 0–${max}`);
        if (num(d.otDays) < 0 || num(d.otDays) > 31) p.push('OT days must be 0–31');
        ['arrears', 'otherAdditions', 'otherRecoveries'].forEach(k => { if (num(d[k]) < 0) p.push(`${k} must be ≥ 0`); });
        if (String(d.remarks || '').length > 250) p.push('remarks max 250 characters');
        return p;
    };

    const clientErrors = useMemo(() => {
        const out = [];
        dirtyRows.forEach(r => rowProblems(r, drafts[r.employeeCode]).forEach(m => out.push(`'${r.employeeCode}': ${m}.`)));
        return out;
    }, [dirtyRows, drafts]);

    const buildBody = () => ({
        payoutMonth,
        rows: dirtyRows.map(r => {
            const d = drafts[r.employeeCode];
            return {
                employeeCode: r.employeeCode,
                reportingDaysOverride: d.reportingDaysOverride === '' ? null : num(d.reportingDaysOverride),
                otDays: num(d.otDays),
                arrears: num(d.arrears),
                otherAdditions: num(d.otherAdditions),
                otherRecoveries: num(d.otherRecoveries),
                remarks: d.remarks || '',
            };
        }),
    });

    // Returns true when there is nothing to save or the save succeeded.
    const save = async () => {
        if (!dirtyRows.length) return true;
        if (clientErrors.length) { setErrors(clientErrors); return false; }
        setSaving(true);
        setErrors([]);
        try {
            const res = await http.put(putPayrollInputs, buildBody());
            if (res.data && !res.data.error) {
                await load();
                return true;
            }
            setErrors(res.data?.errors?.length ? res.data.errors : [res.data?.message || 'Failed to save inputs']);
            return false;
        } catch (err) {
            const apiErrors = err?.response?.data?.errors;
            setErrors(Array.isArray(apiErrors) && apiErrors.length ? apiErrors : [apiErrorMessage(err, 'Failed to save inputs')]);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleSaveAndCalculate = async () => {
        if (await save()) onCalculate?.();
    };

    const estGross = (r, d) => {
        const days = d.reportingDaysOverride === '' ? Number(r.systemReportingDays) || 0 : num(d.reportingDaysOverride);
        if (r.payType === 'Daily') return Math.round(num(r.dailyRate) * days * 100) / 100;
        return Math.round((num(r.monthlyGross) / 30) * days * 100) / 100;
    };

    const head = (label, tip) => (
        <TableCell sx={{
            fontWeight: 700, fontSize: '10.5px', color: '#5B7A94', bgcolor: '#F3F8FC',
            textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap', py: 1,
            borderBottom: '1px solid #E1EAF1',
        }}>
            {tip ? <Tooltip arrow title={tip}><span>{label}</span></Tooltip> : label}
        </TableCell>
    );

    const busy = saving || !!calculating;

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            maxWidth="xl"
            fullWidth
            slotProps={{ paper: { sx: { borderRadius: '7px', overflow: 'hidden', height: '88vh' } } }}
        >
            <Box sx={{
                px: 2.5, py: 1.8,
                background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, #fff 70%)`,
                borderBottom: `1px solid ${PRIMARY_BORDER}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: '7px', bgcolor: '#fff', border: `1px solid ${PRIMARY_BORDER}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <EditNoteRoundedIcon sx={{ color: PRIMARY, fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>
                            Payroll Inputs — {data?.payoutMonth || monthLabel}
                        </Typography>
                        <Typography sx={{ fontSize: '11.5px', color: '#6B7280', mt: 0.3 }}>
                            {data?.attendanceMonth ? `Attendance of ${data.attendanceMonth} · ` : ''}
                            Reporting days, OT days and monthly adjustments for each employee
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search employee…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#9CA3AF' }} /></InputAdornment>,
                                sx: { height: 32, fontSize: 12, borderRadius: '50px', bgcolor: '#fff', width: 220 },
                            },
                        }}
                    />
                    <IconButton
                        onClick={onClose}
                        disabled={busy}
                        sx={{ width: 32, height: 32, borderRadius: '7px', bgcolor: '#fff', border: '1px solid #E5E7EB' }}
                    >
                        <CloseIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                    </IconButton>
                </Box>
            </Box>

            <DialogContent sx={{ p: 0, bgcolor: '#F9FAFB', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 2, pt: 1.5 }}>
                    {missing.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 1, fontSize: 12.5 }}>
                            {missing.length} employee{missing.length === 1 ? ' has' : 's have'} no salary structure and will block Calculate
                            {' '}({missing.slice(0, 5).map(m => `${m.name || m.employeeCode}`).join(', ')}{missing.length > 5 ? '…' : ''}).
                            Create one under Payroll Setup → Salary Structures.
                        </Alert>
                    )}
                    {data && !editable && (
                        <Alert severity="info" sx={{ mb: 1, fontSize: 12.5 }}>
                            Inputs are frozen — the cycle is {data.status}. Roll back to edit.
                        </Alert>
                    )}
                    {loadError && <Alert severity="error" sx={{ mb: 1, fontSize: 12.5 }}>{loadError}</Alert>}
                    {errors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 1, fontSize: 12.5 }}>
                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {errors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
                                {errors.length > 8 && <li>…and {errors.length - 8} more</li>}
                            </Box>
                        </Alert>
                    )}
                </Box>

                <TableContainer sx={{ flex: 1, px: 2, pb: 1 }}>
                    <Table stickyHeader size="small" sx={{ bgcolor: '#fff', border: '1px solid #E1EAF1' }}>
                        <TableHead>
                            <TableRow>
                                {head('Employee')}
                                {head('Pay')}
                                {head('Present / Half / Leave', 'Working days · present · half days · paid leave. LOP = working − (present + ½ half + paid leave)')}
                                {head('LOP')}
                                {head('System days', '30 − LOP (Monthly) or paid days (Daily)')}
                                {head('Reporting days', 'Leave blank to use the system days')}
                                {head('OT days', 'Paid at one day’s pay each')}
                                {head('Arrears ₹')}
                                {head('Other additions ₹')}
                                {head('Other recoveries ₹', 'Fines, damages — not salary advances; those recover themselves')}
                                {head('Remarks')}
                                {head('Est. earned gross')}
                                {head('Saved')}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRowsSkeleton rows={6} cols={13} />
                            ) : visibleRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={13} align="center" sx={{ py: 6, color: '#6B7280', fontSize: 12.5 }}>
                                        {loadError ? '' : search ? 'No matching employees' : 'No employees with a salary structure'}
                                    </TableCell>
                                </TableRow>
                            ) : visibleRows.map(r => {
                                const d = drafts[r.employeeCode] || toDraft(r);
                                const dirty = isDirty(d, r);
                                const problems = rowProblems(r, d);
                                const overridden = d.reportingDaysOverride !== ''
                                    && Math.abs(num(d.reportingDaysOverride) - Number(r.systemReportingDays)) > 0.0001;
                                const cell = { borderBottom: '1px solid #EEF0F6', py: 0.7 };
                                const money = (field, w = 96) => (
                                    <TextField
                                        size="small" type="number" value={d[field]} disabled={!editable}
                                        error={num(d[field]) < 0}
                                        onChange={(e) => setField(r.employeeCode, field, e.target.value)}
                                        sx={{ width: w, ...cellInput }}
                                        slotProps={{ input: { inputProps: { min: 0, step: 'any' } } }}
                                    />
                                );
                                return (
                                    <TableRow key={r.employeeCode} sx={{ bgcolor: dirty ? '#FFFBEB' : '#fff', '&:hover': { bgcolor: dirty ? '#FEF3C7' : '#F3F8FC' } }}>
                                        <TableCell sx={cell}>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>{r.name}</Typography>
                                            <Typography sx={{ fontSize: 10, color: '#9CA3AF' }}>
                                                {r.employeeCode}{r.designation ? ` · ${r.designation}` : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={cell}>
                                            <Chip
                                                size="small"
                                                label={r.payType === 'Daily' ? 'Daily' : 'Monthly'}
                                                sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: r.payType === 'Daily' ? '#FFF7ED' : PRIMARY_LIGHT, color: r.payType === 'Daily' ? '#C2410C' : PRIMARY }}
                                            />
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: PRIMARY_DARK, mt: 0.3 }}>
                                                {r.payType === 'Daily' ? `${formatINR(r.dailyRate)}/day` : formatINR(r.monthlyGross)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ ...cell, fontSize: 11.5, color: '#6B7280', whiteSpace: 'nowrap' }}>
                                            {r.workingDays} · {r.present} · {r.halfDays} · {r.paidLeave}
                                        </TableCell>
                                        <TableCell sx={{ ...cell, fontSize: 12, color: Number(r.lop) > 0 ? '#DC2626' : '#6B7280', fontWeight: 700 }}>{r.lop}</TableCell>
                                        <TableCell sx={{ ...cell, fontSize: 12.5, fontWeight: 800 }}>{r.systemReportingDays}</TableCell>
                                        <TableCell sx={cell}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small" type="number" value={d.reportingDaysOverride} disabled={!editable}
                                                    placeholder={String(r.systemReportingDays)}
                                                    error={d.reportingDaysOverride !== '' && (num(d.reportingDaysOverride) < 0 || num(d.reportingDaysOverride) > (Number(r.maxDays) || 30))}
                                                    onChange={(e) => setField(r.employeeCode, 'reportingDaysOverride', e.target.value)}
                                                    sx={{ width: 84, ...cellInput }}
                                                    slotProps={{ input: { inputProps: { min: 0, max: r.maxDays || 30, step: 0.01 } } }}
                                                />
                                                {overridden && (
                                                    <Tooltip arrow title={`Differs from system days (${r.systemReportingDays})`}>
                                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#F59E0B' }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={cell}>
                                            <Tooltip arrow title={`× ${formatINR(r.payType === 'Daily' ? r.dailyRate : num(r.monthlyGross) / 30)} per day`}>
                                                <TextField
                                                    size="small" type="number" value={d.otDays} disabled={!editable}
                                                    error={num(d.otDays) < 0 || num(d.otDays) > 31}
                                                    onChange={(e) => setField(r.employeeCode, 'otDays', e.target.value)}
                                                    sx={{ width: 70, ...cellInput }}
                                                    slotProps={{ input: { inputProps: { min: 0, max: 31, step: 0.01 } } }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={cell}>{money('arrears')}</TableCell>
                                        <TableCell sx={cell}>{money('otherAdditions')}</TableCell>
                                        <TableCell sx={cell}>{money('otherRecoveries')}</TableCell>
                                        <TableCell sx={cell}>
                                            <TextField
                                                size="small" value={d.remarks} disabled={!editable}
                                                error={String(d.remarks || '').length > 250}
                                                onChange={(e) => setField(r.employeeCode, 'remarks', e.target.value)}
                                                sx={{ width: 160, ...cellInput }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ ...cell, fontSize: 12.5, fontWeight: 700, color: PRIMARY_DARK, whiteSpace: 'nowrap' }}>
                                            {formatINR(estGross(r, d))}
                                        </TableCell>
                                        <TableCell sx={cell}>
                                            {dirty || problems.length ? (
                                                <Chip size="small" label={problems.length ? 'Invalid' : 'Unsaved'} sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: problems.length ? '#FEF2F2' : '#FEF3C7', color: problems.length ? '#DC2626' : '#B45309' }} />
                                            ) : r.hasInput ? (
                                                <Tooltip arrow title={`${r.updatedBy || ''}${r.updatedOn ? ` · ${r.updatedOn}` : ''}`}>
                                                    <CheckCircleIcon sx={{ fontSize: 17, color: '#16A34A' }} />
                                                </Tooltip>
                                            ) : (
                                                <Typography sx={{ fontSize: 11, color: '#CBD5E1' }}>—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #E5E7EB', bgcolor: '#fff', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                    {editable
                        ? `${dirtyRows.length} unsaved change${dirtyRows.length === 1 ? '' : 's'} · ${rows.length} employees`
                        : `${rows.length} employees`}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#374151', border: '1px solid #E5E7EB', borderRadius: '7px', px: 2, height: 36 }}>
                        {editable ? 'Cancel' : 'Close'}
                    </Button>
                    {editable && (
                        <>
                            <Button
                                onClick={async () => { await save(); }}
                                disabled={busy || loading || dirtyRows.length === 0}
                                startIcon={saving ? <CircularProgress size={14} /> : null}
                                sx={{ textTransform: 'none', fontSize: 13, fontWeight: 700, color: PRIMARY_DARK, bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`, borderRadius: '7px', px: 2, height: 36 }}
                            >
                                Save inputs
                            </Button>
                            <Button
                                variant="contained" disableElevation
                                onClick={handleSaveAndCalculate}
                                disabled={busy || loading || missing.length > 0 || rows.length === 0}
                                startIcon={calculating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CalculateOutlinedIcon sx={{ fontSize: 18 }} />}
                                sx={{ textTransform: 'none', fontSize: 13, fontWeight: 700, bgcolor: PRIMARY, borderRadius: '7px', px: 2.4, height: 36, '&:hover': { bgcolor: PRIMARY_DARK }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}
                            >
                                {calculating ? 'Calculating…' : 'Save & Calculate'}
                            </Button>
                        </>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
}
